var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, useRef, useMemo, memo, } from 'react';
import classNames from 'classnames';
import useTrolleyActions from './use-trolley-actions';
import { KEY, keyBoardEventHasKey } from '../../../utils/keyboard';
import useOnClickOutside from '../../../utils/use-on-click-outside';
import useSupercedingTimer from '../../../utils/use-superceding-timer';
import { isAppleTouchDevice } from '../../../utils/browser';
import { TROLLEY_TEXT } from '../constants';
import { TrolleyButtons } from './TrolleyButtons';
import { formatAmount } from './utils';
import { LinkAsButton } from '../../LinkAsButton';
import styles from './TrolleyControls.module.scss';
import DefaultUoMSelector from './UoMSelector';
var recentlyUpdatedTimeout = 2000;
export var TrolleyControls = memo(function (_a) {
    var _b, _c;
    var _d, _e;
    var _f = _a.product, isPersonalisableProduct = _f.isPersonalisableProduct, productLinkProps = _f.productLinkProps, productName = _f.productName, productId = _f.productId, uoms = _f.uoms, lineNumber = _f.lineNumber, isDisabled = _a.isDisabled, onAdd = _a.onAdd, onUpdate = _a.onUpdate, trolleyQuantity = _a.trolleyQuantity, CustomUoMSelector = _a.UoMSelector;
    var inputRef = useRef(null);
    var plusButtonRef = useRef(null);
    var addButtonRef = useRef(null);
    var insideRef = useRef(null);
    var uomsById = useMemo(function () {
        return Object.values(uoms).reduce(function (result, uom) {
            var _a;
            return (__assign(__assign({}, result), (_a = {}, _a[uom.id] = uom, _a)));
        }, {});
    }, [uoms]);
    var _g = useTrolleyActions(trolleyQuantity, uomsById), desiredAmount = _g.desiredAmount, desiredUomId = _g.desiredUomId, selectDesiredUom = _g.selectDesiredUom, updateDesiredAmount = _g.updateDesiredAmount, isEditingTrolleyAmount = _g.isEditingTrolleyAmount, setEditingTrolleyAmount = _g.setEditingTrolleyAmount;
    var updateFocusTimeout = useSupercedingTimer().updateTimeout;
    var _h = useState(false), isRecentlyUpdated = _h[0], setRecentlyUpdated = _h[1];
    var _j = useState(null), recentlyUpdatedTimer = _j[0], setRecentlyUpdatedTimer = _j[1];
    var _k = useState(false), btnAutoFocus = _k[0], setBtnAutoFocus = _k[1];
    var isProductInTrolley = !!trolleyQuantity.amount;
    var isWeightUom = desiredUomId === ((_d = uoms.weight) === null || _d === void 0 ? void 0 : _d.id);
    var showInputField = isProductInTrolley || isWeightUom;
    var showUomSelector = !isPersonalisableProduct && uoms.weight && uoms.item;
    useEffect(function () {
        if (desiredAmount === trolleyQuantity.amount &&
            desiredUomId === trolleyQuantity.uomId &&
            isEditingTrolleyAmount &&
            isRecentlyUpdated) {
            var timer = setTimeout(function () {
                setRecentlyUpdated(false);
                setEditingTrolleyAmount(false);
            }, recentlyUpdatedTimeout);
            setRecentlyUpdatedTimer(timer);
        }
    }, [
        desiredAmount,
        desiredUomId,
        isRecentlyUpdated,
        isEditingTrolleyAmount,
        setRecentlyUpdated,
        setEditingTrolleyAmount,
        setRecentlyUpdatedTimer,
        trolleyQuantity.amount,
        trolleyQuantity.uomId,
    ]);
    var handleAddToTrolley = useCallback(function (event) {
        onAdd({
            amount: desiredAmount && typeof desiredAmount === 'string'
                ? parseFloat(desiredAmount)
                : desiredAmount,
            uomId: desiredUomId,
        }, event);
        setEditingTrolleyAmount(false);
        updateFocusTimeout(function () {
            plusButtonRef.current.focus();
        }, 150);
    }, [desiredAmount, desiredUomId, onAdd, setEditingTrolleyAmount, updateFocusTimeout]);
    var incrementInTrolley = useCallback(function (event) {
        onUpdate({
            amount: trolleyQuantity.amount + uomsById[trolleyQuantity.uomId].increment,
            uomId: trolleyQuantity.uomId,
        }, event);
    }, [onUpdate, trolleyQuantity.amount, trolleyQuantity.uomId, uomsById]);
    var decrementInTrolley = useCallback(function (event) {
        var newAmount = parseFloat((trolleyQuantity.amount - uomsById[trolleyQuantity.uomId].increment).toFixed(1));
        setBtnAutoFocus(true);
        onUpdate({
            amount: newAmount > 0 ? newAmount : 0,
            uomId: trolleyQuantity.uomId,
        }, event);
    }, [trolleyQuantity.amount, trolleyQuantity.uomId, onUpdate, uomsById]);
    var handleUpdateInTrolley = useCallback(function (event) {
        if (!desiredAmount) {
            onUpdate({
                amount: 0,
                uomId: desiredUomId,
            }, event);
        }
        else {
            onUpdate({
                amount: typeof desiredAmount === 'string' ? parseFloat(desiredAmount) : desiredAmount,
                uomId: desiredUomId,
            }, event);
        }
        if (desiredAmount) {
            setRecentlyUpdated(true);
        }
        if (desiredAmount === '' || desiredAmount === 0) {
            setEditingTrolleyAmount(false);
        }
    }, [onUpdate, desiredAmount, desiredUomId, setEditingTrolleyAmount]);
    var handleUpdateKeyPress = useCallback(function (event) {
        if (keyBoardEventHasKey(event, KEY.ENTER)) {
            setBtnAutoFocus(true);
        }
    }, []);
    // TODO refactor TrolleyControls to use a custom hook
    var handleInputKeyDown = useCallback(function (event) {
        if (keyBoardEventHasKey(event, KEY.ENTER)) {
            if (isProductInTrolley) {
                handleUpdateInTrolley(event);
                if (desiredAmount === 0 || desiredAmount === '') {
                    setBtnAutoFocus(true);
                }
            }
            else {
                handleAddToTrolley(event);
                setBtnAutoFocus(true);
            }
        }
    }, [handleAddToTrolley, desiredAmount, isProductInTrolley, handleUpdateInTrolley]);
    var selectUom = useCallback(function (uom) {
        var focusInput = !isAppleTouchDevice && !isProductInTrolley && !isWeightUom;
        if (recentlyUpdatedTimer) {
            clearTimeout(recentlyUpdatedTimer);
            setRecentlyUpdatedTimer(null);
            setEditingTrolleyAmount(false);
            setRecentlyUpdated(false);
        }
        selectDesiredUom(uom);
        if (focusInput) {
            updateFocusTimeout(function () {
                inputRef.current.focus();
            }, 100);
        }
    }, [
        isProductInTrolley,
        isWeightUom,
        recentlyUpdatedTimer,
        selectDesiredUom,
        setEditingTrolleyAmount,
        updateFocusTimeout,
    ]);
    var selectAmount = useCallback(function (event) {
        var value = event.target.value;
        if (recentlyUpdatedTimer) {
            clearTimeout(recentlyUpdatedTimer);
            setRecentlyUpdatedTimer(null);
            setEditingTrolleyAmount(false);
            setRecentlyUpdated(false);
        }
        var newAmount = formatAmount(uomsById[desiredUomId], value);
        if (+newAmount <= uomsById[desiredUomId].maximum) {
            updateDesiredAmount(newAmount);
        }
    }, [recentlyUpdatedTimer, uomsById, desiredUomId, setEditingTrolleyAmount, updateDesiredAmount]);
    var handleFocus = function () { return inputRef.current.select(); };
    useEffect(function () {
        if (btnAutoFocus && !isProductInTrolley) {
            setBtnAutoFocus(false);
            updateFocusTimeout(function () {
                addButtonRef.current.focus();
            }, 100);
        }
        else if (btnAutoFocus && isRecentlyUpdated) {
            setBtnAutoFocus(false);
            updateFocusTimeout(function () {
                plusButtonRef.current.focus();
            }, recentlyUpdatedTimeout + 150);
        }
    }, [desiredAmount, isProductInTrolley, btnAutoFocus, isRecentlyUpdated, updateFocusTimeout]);
    useOnClickOutside([insideRef], function () {
        setEditingTrolleyAmount(false);
    });
    var UoMSelector = useMemo(function () {
        if (typeof CustomUoMSelector !== 'function')
            return DefaultUoMSelector;
        return CustomUoMSelector;
    }, [CustomUoMSelector]);
    return (React.createElement("div", { ref: insideRef, className: styles.trolleyControls, "data-testid": "trolleyControls" },
        showUomSelector ? (React.createElement(UoMSelector, { productId: productId, lineNumber: lineNumber, uoms: uoms, desiredUomId: desiredUomId, selectUom: selectUom })) : null,
        isPersonalisableProduct ? (React.createElement(LinkAsButton, __assign({ className: styles.viewProductButton, "data-testid": TROLLEY_TEXT.viewLink }, productLinkProps, { label: TROLLEY_TEXT.viewLink, theme: "secondary" }))) : (React.createElement("div", { className: classNames(styles.form, (_b = {}, _b[styles.withUomSelector] = showUomSelector, _b)), "data-testid": "amountControls" },
            React.createElement("label", { "data-testid": "inputProductAmountWrapper", className: classNames((_c = {},
                    _c[styles.inputWrapper] = true,
                    _c[styles.showUom] = desiredUomId === ((_e = uoms.weight) === null || _e === void 0 ? void 0 : _e.id),
                    _c[styles.hide] = !showInputField,
                    _c)) },
                React.createElement("input", { "aria-label": "".concat(uomsById[desiredUomId].ariaLabel, " to add to trolley"), ref: inputRef, className: styles.input, "data-testid": "inputProductAmount", type: "text", inputMode: uomsById[desiredUomId].allowFractional ? 'decimal' : 'numeric', max: uomsById[desiredUomId].maximum, min: uomsById[desiredUomId].minimum, value: desiredAmount, onKeyDown: handleInputKeyDown, onChange: selectAmount, onFocus: handleFocus }),
                React.createElement("div", { className: styles.inputUom }, uomsById[desiredUomId].label)),
            React.createElement(TrolleyButtons, __assign({}, {
                isRecentlyUpdated: isRecentlyUpdated,
                isProductInTrolley: isProductInTrolley,
                isEditingTrolleyAmount: isEditingTrolleyAmount,
                isDisabled: isDisabled,
                handleUpdateKeyPress: handleUpdateKeyPress,
                updateInTrolley: handleUpdateInTrolley,
                decrementInTrolley: decrementInTrolley,
                incrementInTrolley: incrementInTrolley,
                addToTrolley: handleAddToTrolley,
                addButtonRef: addButtonRef,
                plusButtonRef: plusButtonRef,
                productName: productName,
            }))))));
});
//# sourceMappingURL=TrolleyControls.js.map